<script setup>
import { computed } from 'vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import RemoveMenuItem from '@/components/ui/RemoveMenuItem.vue'
import Spinner from '@/components/ui/Spinner.vue'
import StatusPill from '@/components/Atoms/StatusPill.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconEdit from '@/components/icon/IconEdit.vue'
import IconMoveToGroup from '@/components/icon/IconMoveToGroup.vue'
import { usePlatformListContext } from './platformListContext'

const props = defineProps({
	platform: {
		type: Object,
		required: true,
	},
	index: {
		type: Number,
		default: 0,
	},
	metadataOpen: {
		type: Boolean,
		default: false,
	},
	hasMetadata: {
		type: Boolean,
		default: false,
	},
})

const emit = defineEmits(['toggle-metadata', 'open-metadata'])

const ctx = usePlatformListContext()
const isGuest = computed(() => !!props.platform.isGuest)
const isYoutubeEventsInvalid = computed(() =>
	!props.platform.isTokenValid && props.platform.template === 'youtube events'
)
const switchDisabled = computed(() =>
	(isGuest.value && ctx.isGuestDestinationRemoved(props.platform))
	|| ctx.cannotTogglePlatforms
	|| !props.platform.isTokenValid
	|| props.platform.tooltipId !== undefined
	|| isYoutubeEventsInvalid.value
)
const metadataDisabled = computed(() =>
	props.platform.tooltipId !== undefined || isYoutubeEventsInvalid.value
)
const showPushStatus = computed(() =>
	props.platform.enabled
	&& props.platform.tooltipId === undefined
	&& ctx.mediaPulse && ctx.mediaPulse.push_stats
)
// OAuth-linked destinations can't change server/key, but their title/description can be edited
function onEdit() {
	// YouTube Events has its own edit modal that can also switch the bound event
	if (props.platform.template === 'youtube events' && props.platform.linkedServiceCreds && !metadataDisabled.value) {
		ctx.requestPlatformModify(props.platform)
		return
	}
	if (props.platform.linkedServiceCreds && props.hasMetadata && !metadataDisabled.value) {
		emit('open-metadata')
		return
	}
	ctx.requestPlatformModify(props.platform)
}
const loopFacebookTooltip = 'Infinite loop mode does not support Facebook, please use Schedule Date time mode instead.'
</script>

<template>
	<div class="mobile-destination-actions flex items-center gap-0 md:gap-2 shrink-0 self-start -mr-1 md:mr-0">
		<div v-if="platform.removingProcessing" class="hidden md:block">
			<Spinner text=" " classes="text-surface-8 my-3" spinner-color="var(--c-dark-8)" spinner-size="14px" />
		</div>
		<div v-else-if="showPushStatus" class="hidden md:inline-block platform-connect-status-container">
			<div class="status-box flex items-center md:mr-2">
				<StatusPill v-if="platform.enabled && platform.connected" variant="success">Connected</StatusPill>
				<StatusPill v-else variant="warning" pulse>Connecting…</StatusPill>
			</div>
		</div>
		<div class="mobile-destination-controls flex flex-nowrap items-center gap-0 md:gap-2" :id="`${platform.tooltipId || platform._id}`">
			<!-- Live/multistream keep metadata in the top actions; scheduled moves it bottom-right in DestinationRow -->
			<FormButton
				v-if="hasMetadata && ctx.streamType !== 'scheduled'"
				size="sm"
				type="secondary"
				class="metadata-toggle-btn !hidden md:!inline-flex"
				:disabled="metadataDisabled"
				@click="emit('toggle-metadata')"
			>
				{{ metadataOpen ? 'Hide metadata' : 'Show metadata' }}
			</FormButton>
			<Tooltip
				:content="loopFacebookTooltip"
				:active="platform.tooltipId !== undefined"
				wrap-trigger
			>
				<FormSwitch
					label-position="left"
					label-styles="text-sm text-surface-9"
					:id="'toggle' + index"
					:disabled="switchDisabled"
					v-model="platform.enabled"
					@change="ctx.onTogglePlatformStatus(platform)"
				/>
			</Tooltip>
			<DropDownMenu
				position="right"
				type="secondary"
				isIcon
				no-border
				:disabled="ctx.deleteBlock"
				class="shrink-0"
			>
				<template #toggle-button>
					<img src="@/assets/icons/settings.svg" alt="Settings">
				</template>
				<template #menu-items>
					<DropDownMenuItem v-if="!isGuest" @click="onEdit">
						<div class="flex items-center">
							<icon-base class="mr-2" fill="none">
								<icon-edit />
							</icon-base>
							Edit
						</div>
					</DropDownMenuItem>
					<DropDownMenuItem v-if="ctx.hasGroups" @click="ctx.requestPlatformMoveToGroup(platform)">
						<div class="flex items-center">
							<IconMoveToGroup class="mr-2" />
							Move to group
						</div>
					</DropDownMenuItem>
					<RemoveMenuItem @confirm="ctx.onInlineRemove(platform)" />
				</template>
			</DropDownMenu>
		</div>
	</div>
</template>
